export interface ColorOption {
  id: string
  name: string
  hex: string
  selected?: boolean
}

export interface PatternOption {
  id: string
  name: string
  thumbnail: string
  priceModifier?: number
  selected?: boolean
}

export interface InteriorOption {
  id: string
  name: string
  thumbnail?: string
  selected?: boolean
  description?: string
  colorOptions?: ColorOption[]
  patternOptions?: PatternOption[]
}

export interface InteriorMenuItem {
  id: string
  name: string
  type: 'category' | 'item' | 'option'
  icon?: string
  children?: InteriorMenuItem[]
  options?: InteriorOption[]
  expanded?: boolean
  selected?: boolean
  isPackage?: boolean
  isGlobalPackage?: boolean
  allowNone?: boolean
}

// Reusable pattern thumbnails (clean line-art SVGs for tile / plank layouts)
const P = {
  // Crosshatch (basketweave brick)
  crosshatch:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='5' y='5' width='40' height='18'/%3E%3Crect x='5' y='23' width='40' height='18'/%3E%3Crect x='45' y='5' width='18' height='40'/%3E%3Crect x='63' y='5' width='18' height='40'/%3E%3Crect x='81' y='5' width='14' height='40'/%3E%3Crect x='5' y='41' width='14' height='40'/%3E%3Crect x='19' y='41' width='40' height='18'/%3E%3Crect x='19' y='59' width='40' height='18'/%3E%3Crect x='59' y='41' width='18' height='40'/%3E%3Crect x='77' y='41' width='18' height='40'/%3E%3Crect x='5' y='77' width='40' height='18'/%3E%3C/g%3E%3C/svg%3E",
  // Stack bond — straight aligned bricks
  stackBond:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='5' y='8' width='42' height='14'/%3E%3Crect x='53' y='8' width='42' height='14'/%3E%3Crect x='5' y='28' width='42' height='14'/%3E%3Crect x='53' y='28' width='42' height='14'/%3E%3Crect x='5' y='48' width='42' height='14'/%3E%3Crect x='53' y='48' width='42' height='14'/%3E%3Crect x='5' y='68' width='42' height='14'/%3E%3Crect x='53' y='68' width='42' height='14'/%3E%3C/g%3E%3C/svg%3E",
  // Running bond / brick bond — offset rows
  runningBond:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='5' y='8' width='42' height='14'/%3E%3Crect x='53' y='8' width='42' height='14'/%3E%3Crect x='-15' y='28' width='40' height='14'/%3E%3Crect x='27' y='28' width='42' height='14'/%3E%3Crect x='75' y='28' width='40' height='14'/%3E%3Crect x='5' y='48' width='42' height='14'/%3E%3Crect x='53' y='48' width='42' height='14'/%3E%3Crect x='-15' y='68' width='40' height='14'/%3E%3Crect x='27' y='68' width='42' height='14'/%3E%3Crect x='75' y='68' width='40' height='14'/%3E%3C/g%3E%3C/svg%3E",
  // Offset / one third drop
  offset:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='5' y='5' width='28' height='42'/%3E%3Crect x='5' y='47' width='28' height='42'/%3E%3Crect x='35' y='-12' width='28' height='42'/%3E%3Crect x='35' y='30' width='28' height='42'/%3E%3Crect x='35' y='72' width='28' height='42'/%3E%3Crect x='65' y='5' width='28' height='42'/%3E%3Crect x='65' y='47' width='28' height='42'/%3E%3C/g%3E%3C/svg%3E",
  // Herringbone (traditional)
  herringbone:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='10' y='10' width='30' height='12' transform='rotate(45 25 16)'/%3E%3Crect x='10' y='30' width='30' height='12' transform='rotate(-45 25 36)'/%3E%3Crect x='40' y='10' width='30' height='12' transform='rotate(45 55 16)'/%3E%3Crect x='40' y='30' width='30' height='12' transform='rotate(-45 55 36)'/%3E%3Crect x='10' y='55' width='30' height='12' transform='rotate(45 25 61)'/%3E%3Crect x='10' y='75' width='30' height='12' transform='rotate(-45 25 81)'/%3E%3Crect x='40' y='55' width='30' height='12' transform='rotate(45 55 61)'/%3E%3Crect x='40' y='75' width='30' height='12' transform='rotate(-45 55 81)'/%3E%3Crect x='70' y='10' width='30' height='12' transform='rotate(45 85 16)'/%3E%3Crect x='70' y='30' width='30' height='12' transform='rotate(-45 85 36)'/%3E%3Crect x='70' y='55' width='30' height='12' transform='rotate(45 85 61)'/%3E%3Crect x='70' y='75' width='30' height='12' transform='rotate(-45 85 81)'/%3E%3C/g%3E%3C/svg%3E",
  // Chevron — angled but aligned tips
  chevron:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Cpath d='M5 25 L50 5 L50 20 L5 40 Z'/%3E%3Cpath d='M50 5 L95 25 L95 40 L50 20 Z'/%3E%3Cpath d='M5 50 L50 30 L50 45 L5 65 Z'/%3E%3Cpath d='M50 30 L95 50 L95 65 L50 45 Z'/%3E%3Cpath d='M5 75 L50 55 L50 70 L5 90 Z'/%3E%3Cpath d='M50 55 L95 75 L95 90 L50 70 Z'/%3E%3C/g%3E%3C/svg%3E",
  // Vertical stack
  verticalStack:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='8' y='5' width='14' height='42'/%3E%3Crect x='8' y='47' width='14' height='42'/%3E%3Crect x='28' y='5' width='14' height='42'/%3E%3Crect x='28' y='47' width='14' height='42'/%3E%3Crect x='48' y='5' width='14' height='42'/%3E%3Crect x='48' y='47' width='14' height='42'/%3E%3Crect x='68' y='5' width='14' height='42'/%3E%3Crect x='68' y='47' width='14' height='42'/%3E%3Crect x='88' y='5' width='14' height='42'/%3E%3Crect x='88' y='47' width='14' height='42'/%3E%3C/g%3E%3C/svg%3E",
  // Plank — long horizontal boards, slight offset
  plank:
    "data:image/svg+xml;utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100' fill='none' stroke='%23334155' stroke-width='1.5'%3E%3Crect width='100' height='100' fill='%23fff'/%3E%3Cg%3E%3Crect x='5' y='10' width='60' height='10'/%3E%3Crect x='65' y='10' width='30' height='10'/%3E%3Crect x='5' y='26' width='25' height='10'/%3E%3Crect x='30' y='26' width='65' height='10'/%3E%3Crect x='5' y='42' width='50' height='10'/%3E%3Crect x='55' y='42' width='40' height='10'/%3E%3Crect x='5' y='58' width='35' height='10'/%3E%3Crect x='40' y='58' width='55' height='10'/%3E%3Crect x='5' y='74' width='65' height='10'/%3E%3Crect x='70' y='74' width='25' height='10'/%3E%3C/g%3E%3C/svg%3E",
}

// Pre-built pattern option sets
export const TILE_PATTERNS: PatternOption[] = [
  {
    id: 'pat-stack-bond',
    name: 'Stack Bond',
    thumbnail: P.stackBond,
    selected: true,
  },
  {
    id: 'pat-running-bond',
    name: 'Running Bond',
    thumbnail: P.runningBond,
    priceModifier: 200,
  },
  {
    id: 'pat-offset',
    name: 'One Third Offset',
    thumbnail: P.offset,
    priceModifier: 300,
  },
  {
    id: 'pat-herringbone',
    name: 'Herringbone',
    thumbnail: P.herringbone,
    priceModifier: 800,
  },
  {
    id: 'pat-chevron',
    name: 'Chevron',
    thumbnail: P.chevron,
    priceModifier: 900,
  },
  {
    id: 'pat-crosshatch',
    name: 'Crosshatch',
    thumbnail: P.crosshatch,
    priceModifier: 700,
  },
  {
    id: 'pat-vertical-stack',
    name: 'Vertical Stack',
    thumbnail: P.verticalStack,
    priceModifier: 250,
  },
]

export const WOOD_PATTERNS: PatternOption[] = [
  { id: 'pat-plank', name: 'Plank', thumbnail: P.plank, selected: true },
  {
    id: 'pat-herringbone',
    name: 'Herringbone',
    thumbnail: P.herringbone,
    priceModifier: 1200,
  },
  {
    id: 'pat-chevron',
    name: 'Chevron',
    thumbnail: P.chevron,
    priceModifier: 1400,
  },
  {
    id: 'pat-brick-bond',
    name: 'Brick Bond',
    thumbnail: P.runningBond,
    priceModifier: 600,
  },
  {
    id: 'pat-stack-bond',
    name: 'Stack Bond',
    thumbnail: P.stackBond,
    priceModifier: 400,
  },
]

// Reusable placeholder thumbnails
const T = {
  a: 'https://cdn.magicpatterns.com/uploads/56xcQygN48ytC9vCWfbKsD/2a3e8f16-fd9a-4893-b4aa-68846e8eee8d.png',
  b: 'https://cdn.magicpatterns.com/uploads/sj8L76yNDFjCGFZWdgGY4N/9d9925e9-c3ea-4a0d-80fb-ffcfee4e866e.png',
  c: 'https://cdn.magicpatterns.com/uploads/4rsTQoSw8cboz4u88BHB6e/31666bba-be57-4389-96b2-845051abc30a.png',
  d: 'https://cdn.magicpatterns.com/uploads/8uMrohCuZZGCZe6mp1Ebh5/bdab08d5-af5f-4768-afba-d5b3c4e4d4f4.png',
  e: 'https://cdn.magicpatterns.com/uploads/rWjZv6Mm4vUpRox9hUrK2K/92c595c0-b3df-4f96-84f9-414aad4eea5b.png',
  f: 'https://cdn.magicpatterns.com/uploads/cZReokz7WQeKktQan8Tx5j/c4f38fb8-8ad6-4d2b-ba7a-5cae3db41a44.png',
  g: 'https://cdn.magicpatterns.com/uploads/mVj6c7zkUqrRquwCmxWcL6/15d95c32-8149-4132-9785-f1bfe49bb891.png',
  h: 'https://cdn.magicpatterns.com/uploads/kpH5AVoc585CmkhtFwT2gu/bb2e92e2-95c2-4917-a8f9-e317d073068e.png',
  i: 'https://cdn.magicpatterns.com/uploads/hA4ppMCeeEnfUr8DdemFhK/80b42e57-750f-436c-8070-ab27d0716845.png',
  j: 'https://cdn.magicpatterns.com/uploads/xefSXa2PcHLjXAgiprF9C2/94335722-d546-4e85-83cf-af86687637c9.png',
}

export const interiorMenuData: InteriorMenuItem[] = [
  {
    id: 'layouts',
    name: 'Layouts',
    type: 'category',
    children: [
      {
        id: 'floorplan-extended',
        name: 'Extended Layout',
        type: 'item',
        options: [
          {
            id: 'fp-ext-standard',
            name: 'Standard',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Cpath d='M60 20L90 40L90 55L30 55L30 40Z' stroke='white' stroke-width='3' stroke-linejoin='round' fill='none'/%3E%3Cpath d='M60 55L90 55L90 95L30 95L30 55Z' stroke='white' stroke-width='3' stroke-linejoin='round' fill='none'/%3E%3Cline x1='30' y1='55' x2='90' y2='55' stroke='white' stroke-width='2' stroke-dasharray='4 3'/%3E%3C/svg%3E",
            selected: true,
          },
          {
            id: 'fp-ext-extended',
            name: 'Extended',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Cpath d='M60 20L95 42L95 57L25 57L25 42Z' stroke='white' stroke-width='3' stroke-linejoin='round' fill='none'/%3E%3Cpath d='M60 57L95 57L95 97L25 97L25 57Z' stroke='white' stroke-width='3' stroke-linejoin='round' fill='none'/%3E%3Cline x1='25' y1='57' x2='95' y2='57' stroke='white' stroke-width='2' stroke-dasharray='4 3'/%3E%3Ccircle cx='82' cy='47' r='4' fill='white' opacity='0.5'/%3E%3Ccircle cx='82' cy='77' r='4' fill='white' opacity='0.5'/%3E%3Cpath d='M75 43L89 43L89 51L75 51' stroke='white' stroke-width='2' stroke-dasharray='3 2' fill='none'/%3E%3Cpath d='M75 73L89 73L89 81L75 81' stroke='white' stroke-width='2' stroke-dasharray='3 2' fill='none'/%3E%3C/svg%3E",
          },
        ],
      },
      {
        id: 'floorplan-basement',
        name: 'Basement',
        type: 'item',
        options: [
          {
            id: 'fp-base-developed',
            name: 'Developed',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Cpath d='M30 85L30 65L45 65L45 50L60 50L60 35L75 35L75 50L90 50L90 85' stroke='white' stroke-width='4' stroke-linecap='round' stroke-linejoin='round' fill='none'/%3E%3C/svg%3E",
            selected: true,
          },
          {
            id: 'fp-base-legal-suite',
            name: 'Legal Suite',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='30' y='50' width='60' height='30' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Crect x='30' y='40' width='25' height='15' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Ccircle cx='75' cy='45' r='8' stroke='white' stroke-width='3.5' fill='none'/%3E%3C/svg%3E",
          },
          {
            id: 'fp-base-legal-2bed',
            name: 'Legal Suite 2 Bed',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='50' width='35' height='28' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Crect x='20' y='40' width='15' height='14' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Crect x='65' y='50' width='35' height='28' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Crect x='65' y='40' width='15' height='14' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Ccircle cx='60' cy='45' r='6' stroke='white' stroke-width='3' fill='none'/%3E%3C/svg%3E",
          },
        ],
      },
      {
        id: 'floorplan-main',
        name: 'Main Floor',
        type: 'item',
        options: [
          {
            id: 'fp-main-standard',
            name: 'Standard',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='25' y='30' width='70' height='55' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Cline x1='60' y1='30' x2='60' y2='85' stroke='white' stroke-width='2.5'/%3E%3Cline x1='25' y1='58' x2='60' y2='58' stroke='white' stroke-width='2.5'/%3E%3C/svg%3E",
            selected: true,
          },
          {
            id: 'fp-main-extended',
            name: 'Extended Living',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='30' width='80' height='55' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Cline x1='55' y1='30' x2='55' y2='85' stroke='white' stroke-width='2.5'/%3E%3Cline x1='20' y1='55' x2='55' y2='55' stroke='white' stroke-width='2.5'/%3E%3Cpath d='M70 50L85 50L85 70L70 70' stroke='white' stroke-width='2' stroke-dasharray='4 3'/%3E%3C/svg%3E",
          },
          {
            id: 'fp-main-open',
            name: 'Open Concept',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='30' width='80' height='55' rx='4' stroke='white' stroke-width='3.5' fill='none'/%3E%3Cline x1='20' y1='58' x2='50' y2='58' stroke='white' stroke-width='2.5'/%3E%3Cline x1='50' y1='58' x2='50' y2='85' stroke='white' stroke-width='2.5'/%3E%3C/svg%3E",
          },
        ],
      },
      {
        id: 'floorplan-ground',
        name: 'Ground Floor',
        type: 'item',
        options: [
          {
            id: 'fp-ground-standard',
            name: 'Standard',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Cpath d='M60 25L90 45L90 85L30 85L30 45Z' stroke='white' stroke-width='3.5' stroke-linejoin='round' fill='none'/%3E%3Crect x='48' y='60' width='24' height='25' rx='2' stroke='white' stroke-width='2.5' fill='none'/%3E%3Cline x1='60' y1='60' x2='60' y2='85' stroke='white' stroke-width='2'/%3E%3C/svg%3E",
            selected: true,
          },
        ],
      },
      {
        id: 'floorplan-upper',
        name: 'Upper Floor',
        type: 'item',
        options: [
          {
            id: 'fp-upper-3bed',
            name: '3 Bedroom',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='28' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='65' y='28' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='35' y='65' width='50' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3C/svg%3E",
            selected: true,
          },
          {
            id: 'fp-upper-4bed',
            name: '4 Bedroom',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='25' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='65' y='25' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='20' y='62' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='65' y='62' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3C/svg%3E",
          },
          {
            id: 'fp-upper-loft',
            name: 'Loft Layout',
            thumbnail:
              "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' fill='none'%3E%3Crect width='120' height='120' rx='12' fill='%231e293b'/%3E%3Crect x='20' y='28' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='65' y='28' width='35' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Crect x='25' y='65' width='70' height='25' rx='3' stroke='white' stroke-width='3' fill='none'/%3E%3Cline x1='60' y1='65' x2='60' y2='90' stroke='white' stroke-width='2' stroke-dasharray='4 3'/%3E%3C/svg%3E",
          },
        ],
      },
    ],
  },
  {
    id: 'packages',
    name: 'Packages',
    type: 'category',
    children: [
      {
        id: 'gpkg-sky',
        name: 'Sky',
        type: 'item',
        isGlobalPackage: true,
      },
      {
        id: 'gpkg-river',
        name: 'River',
        type: 'item',
        isGlobalPackage: true,
      },
      {
        id: 'gpkg-forest',
        name: 'Forest',
        type: 'item',
        isGlobalPackage: true,
      },
    ],
  },
  {
    id: 'main-options',
    name: 'Main Options',
    type: 'category',
    children: [
      {
        id: 'general',
        name: 'General',
        type: 'item',
      },
      {
        id: 'kitchen',
        name: 'Kitchen',
        type: 'item',
        children: [
          {
            id: 'kitchen-packages',
            name: 'Kitchen Packages',
            type: 'item',
            isPackage: true,
          },
          { id: 'kitchen-layout', name: 'Layout', type: 'item' },
          {
            id: 'refrigerator',
            name: 'Refrigerator',
            type: 'item',
            options: [
              {
                id: 'fridge-standard',
                name: 'Standard 30"',
                thumbnail: T.a,
                selected: true,
                colorOptions: [
                  {
                    id: 'fs-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'fs-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'fs-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'fridge-french-door',
                name: 'French Door 36"',
                thumbnail: T.b,
                colorOptions: [
                  {
                    id: 'ffd-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'ffd-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'ffd-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'ffd-slate', name: 'Slate', hex: '#708090' },
                ],
              },
              {
                id: 'fridge-side-by-side',
                name: 'Side-by-Side',
                thumbnail: T.c,
                colorOptions: [
                  {
                    id: 'fsbs-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'fsbs-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'fridge-counter-depth',
                name: 'Counter Depth',
                thumbnail: T.d,
                colorOptions: [
                  {
                    id: 'fcd-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'fcd-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'fcd-white', name: 'White', hex: '#F5F5F5' },
                  {
                    id: 'fcd-fingerprint',
                    name: 'Fingerprint Resistant',
                    hex: '#B8B8B8',
                  },
                ],
              },
              {
                id: 'fridge-smart',
                name: 'Smart Hub',
                thumbnail: T.e,
                colorOptions: [
                  {
                    id: 'fsh-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  {
                    id: 'fsh-black-ss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                  },
                  { id: 'fsh-white', name: 'White', hex: '#F5F5F5' },
                  { id: 'fsh-navy', name: 'Family Hub Navy', hex: '#1B3A5C' },
                ],
              },
              {
                id: 'fridge-panel-ready',
                name: 'Panel Ready',
                thumbnail: T.f,
                colorOptions: [
                  {
                    id: 'fpr-panel',
                    name: 'Custom Panel',
                    hex: '#D4C5A9',
                    selected: true,
                  },
                ],
              },
            ],
          },
          { id: 'cooktop', name: 'Cooktop', type: 'item' },
          {
            id: 'range-oven',
            name: 'Range Oven',
            type: 'item',
            options: [
              {
                id: 'standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/ipGLrgJ5dnUHzeX467UrRX/99451023-beef-4830-b2e4-6b5c5bbc05fa.png',
                selected: true,
                colorOptions: [
                  {
                    id: 'ro-std-white',
                    name: 'White',
                    hex: '#F5F5F5',
                    selected: true,
                  },
                  { id: 'ro-std-black', name: 'Black', hex: '#2D2D2D' },
                ],
              },
              {
                id: 'level-1',
                name: 'Stainless Steel',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/7qRjsZBrCQMtsgwG6dR6aa/a12724ab-875b-49f0-82d7-cbbbc5f8d329.png',
                colorOptions: [
                  {
                    id: 'ro-l1-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                ],
              },
              {
                id: 'level-2',
                name: 'Professional',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/rBmiA4HJkMsYLa3DUGGUeP/a38be756-541f-4f67-b58f-3e7cff68d49c.png',
                colorOptions: [
                  {
                    id: 'ro-l2-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'ro-l2-matte', name: 'Matte Black', hex: '#1A1A1A' },
                ],
              },
              {
                id: 'level-3',
                name: 'Chef Series',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/i3anGUuRyR4DYNoRawPTEF/ba5cadb3-2c6f-4c61-b770-e245581bd9ca.png',
                colorOptions: [
                  {
                    id: 'ro-l3-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                  { id: 'ro-l3-matte', name: 'Matte Black', hex: '#1A1A1A' },
                  { id: 'ro-l3-burgundy', name: 'Burgundy', hex: '#722F37' },
                ],
              },
              {
                id: 'level-4',
                name: 'Commercial Grade',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/j59ZjLMVTNqtgapFimQc1E/08ea5d1f-e4b6-4d72-8184-56c394b9cb43.png',
                colorOptions: [
                  {
                    id: 'ro-l4-stainless',
                    name: 'Stainless',
                    hex: '#C0C0C0',
                    selected: true,
                  },
                ],
              },
              {
                id: 'black-electric',
                name: 'Black Stainless',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/rCCBzCc61oxdxptZB8CK3X/7e166a12-77dc-4c11-b902-19ee335133af.png',
                colorOptions: [
                  {
                    id: 'ro-be-blackss',
                    name: 'Black Stainless',
                    hex: '#3A3A3A',
                    selected: true,
                  },
                ],
              },
              {
                id: 'black-gas',
                name: 'Matte Black',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/6x4JkKCohmWMYizkdUVv5H/c5ee6763-a055-4b4c-9623-560075c646ed.png',
                colorOptions: [
                  {
                    id: 'ro-bg-matte',
                    name: 'Matte Black',
                    hex: '#1A1A1A',
                    selected: true,
                  },
                ],
              },
            ],
          },
          {
            id: 'dishwasher',
            name: 'Dishwasher',
            type: 'item',
            options: [
              {
                id: 'dw-standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/8WnWtNRoYDXghwDDYKxMY1/3a7bf4b8-f2ce-43ab-812d-d2135155715f.png',
                selected: true,
              },
              {
                id: 'dw-quiet',
                name: 'Ultra Quiet',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/9HgKuy26y4nFyrfLCdGjh9/43af1ca4-cf13-471b-bebe-8bd2e03fcbfb.png',
              },
              {
                id: 'dw-third-rack',
                name: 'Third Rack',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/trdsaseMYftX5ReXeRVWmo/728deced-e8d7-48dc-a7f2-8c641afae38b.png',
              },
              {
                id: 'dw-panel-ready',
                name: 'Panel Ready',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/gXch4QkMwxNQ9Gf4G5EAdJ/b394dd48-02c4-4c34-a060-d76f1604fa7c.png',
              },
              {
                id: 'dw-smart',
                name: 'Smart Connect',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/iQkCQnTBXVuPRJZw5CWi23/a3b5acff-a1c0-4442-84b1-286862e88499.png',
              },
            ],
          },
          { id: 'wall-oven', name: 'Wall Oven', type: 'item' },
          {
            id: 'microwave',
            name: 'Microwave',
            type: 'item',
            options: [
              {
                id: 'mw-standard',
                name: 'Standard',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/7J9vR4HKQ2tpt65jg7G16W/74bb9c06-756a-4000-8501-7b776559d1c5.png',
                selected: true,
              },
              {
                id: 'mw-over-range',
                name: 'Over-the-Range',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/raVYgQuqvg9FTcFXiVoWdF/d1779e5a-0f26-4813-b30b-c76d034a46e4.png',
              },
              {
                id: 'mw-drawer',
                name: 'Drawer Style',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/9g5j9FPCgRFZUjU4MVxTD6/dcf7809d-faed-42b2-844a-3dab62702865.png',
              },
              {
                id: 'mw-built-in',
                name: 'Built-In',
                thumbnail:
                  'https://cdn.magicpatterns.com/uploads/4o8ktMaBmPjywuSkMVi4Um/e2e961b3-6f11-40d0-b1be-f04ad41d6c36.png',
              },
            ],
          },
          { id: 'hood-fan', name: 'Hood Fan', type: 'item' },
          {
            id: 'cabinet-profile',
            name: 'Cabinet Profile',
            type: 'item',
            options: [
              {
                id: 'cp-shaker',
                name: 'Shaker',
                thumbnail: T.g,
                selected: true,
              },
              { id: 'cp-flat', name: 'Flat Panel', thumbnail: T.h },
              { id: 'cp-raised', name: 'Raised Panel', thumbnail: T.i },
              { id: 'cp-beadboard', name: 'Beadboard', thumbnail: T.j },
            ],
            children: [
              {
                id: 'handle-model',
                name: 'Handle Model',
                type: 'item',
                options: [
                  {
                    id: 'hm-bar',
                    name: 'Bar Pull',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'hm-knob', name: 'Knob', thumbnail: T.b },
                  { id: 'hm-cup', name: 'Cup Pull', thumbnail: T.c },
                  { id: 'hm-edge', name: 'Edge Pull', thumbnail: T.d },
                  { id: 'hm-ring', name: 'Ring Pull', thumbnail: T.e },
                ],
              },
              {
                id: 'handle-material',
                name: 'Handle Material',
                type: 'item',
                options: [
                  {
                    id: 'hmat-nickel',
                    name: 'Brushed Nickel',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'hmat-black', name: 'Matte Black', thumbnail: T.h },
                  {
                    id: 'hmat-chrome',
                    name: 'Polished Chrome',
                    thumbnail: T.i,
                  },
                  {
                    id: 'hmat-brass',
                    name: 'Antique Brass',
                    thumbnail: T.j,
                  },
                  {
                    id: 'hmat-bronze',
                    name: 'Oil-Rubbed Bronze',
                    thumbnail: T.f,
                  },
                ],
              },
            ],
          },
          {
            id: 'upper-cabinet-finish',
            name: 'Upper Cabinet Finish',
            type: 'item',
          },
          {
            id: 'lower-cabinet-finish',
            name: 'Lower Cabinet Finish',
            type: 'item',
          },
          {
            id: 'countertop',
            name: 'Countertop',
            type: 'item',
            options: [
              {
                id: 'ct-laminate',
                name: 'Laminate',
                thumbnail: T.g,
                selected: true,
              },
              { id: 'ct-solid-surface', name: 'Solid Surface', thumbnail: T.b },
              { id: 'ct-ceramic-tile', name: 'Ceramic Tile', thumbnail: T.c },
              { id: 'ct-butcher-block', name: 'Butcher Block', thumbnail: T.d },
              { id: 'ct-concrete', name: 'Concrete', thumbnail: T.e },
              {
                id: 'ct-recycled-glass',
                name: 'Recycled Glass',
                thumbnail: T.f,
              },
              { id: 'ct-granite', name: 'Granite', thumbnail: T.h },
              { id: 'ct-soapstone', name: 'Soapstone', thumbnail: T.a },
              { id: 'ct-quartz', name: 'Quartz', thumbnail: T.i },
              { id: 'ct-quartzite', name: 'Quartzite', thumbnail: T.b },
              { id: 'ct-marble', name: 'Marble', thumbnail: T.j },
              {
                id: 'ct-porcelain-slab',
                name: 'Porcelain Slab',
                thumbnail: T.c,
              },
              { id: 'ct-dekton', name: 'Dekton Ultra', thumbnail: T.d },
              { id: 'ct-calacatta', name: 'Calacatta Gold', thumbnail: T.e },
            ],
          },
          { id: 'backsplash', name: 'Backsplash', type: 'item' },
          {
            id: 'backsplash',
            name: 'Backsplash',
            type: 'item',
            options: [
              {
                id: 'bs-subway-white',
                name: 'White Subway Tile',
                thumbnail: T.g,
                selected: true,
              },
              {
                id: 'bs-subway-gray',
                name: 'Gray Subway Tile',
                thumbnail: T.h,
              },
              {
                id: 'bs-herringbone',
                name: 'Herringbone Mosaic',
                thumbnail: T.i,
              },
              { id: 'bs-hexagon', name: 'Hexagon Tile', thumbnail: T.j },
              { id: 'bs-marble-slab', name: 'Marble Slab', thumbnail: T.a },
              { id: 'bs-glass-mosaic', name: 'Glass Mosaic', thumbnail: T.b },
              { id: 'bs-zellige', name: 'Zellige Tile', thumbnail: T.c },
              { id: 'bs-penny-round', name: 'Penny Round', thumbnail: T.d },
              { id: 'bs-natural-stone', name: 'Natural Stone', thumbnail: T.e },
            ],
          },
          {
            id: 'flooring',
            name: 'Flooring',
            type: 'item',
            options: [
              {
                id: 'fl-hardwood-oak',
                name: 'White Oak Hardwood',
                thumbnail: T.g,
                selected: true,
                patternOptions: WOOD_PATTERNS,
              },
              {
                id: 'fl-hardwood-walnut',
                name: 'Walnut Hardwood',
                thumbnail: T.h,
                patternOptions: WOOD_PATTERNS,
              },
              {
                id: 'fl-engineered-hickory',
                name: 'Engineered Hickory',
                thumbnail: T.i,
                patternOptions: WOOD_PATTERNS,
              },
              {
                id: 'fl-lvp-standard',
                name: 'Luxury Vinyl Plank',
                thumbnail: T.j,
                patternOptions: WOOD_PATTERNS,
              },
              {
                id: 'fl-porcelain-tile',
                name: 'Porcelain Tile',
                thumbnail: T.a,
                patternOptions: TILE_PATTERNS,
              },
              {
                id: 'fl-ceramic-tile',
                name: 'Ceramic Tile',
                thumbnail: T.b,
                patternOptions: TILE_PATTERNS,
              },
              {
                id: 'fl-natural-stone',
                name: 'Natural Stone',
                thumbnail: T.c,
                patternOptions: TILE_PATTERNS,
              },
              {
                id: 'fl-herringbone',
                name: 'Herringbone Parquet',
                thumbnail: T.d,
                patternOptions: WOOD_PATTERNS,
              },
            ],
          },
          { id: 'faucet', name: 'Faucet', type: 'item' },
          { id: 'faucet-finish', name: 'Faucet Finish', type: 'item' },
          { id: 'sink', name: 'Sink', type: 'item' },
          { id: 'sink-finish', name: 'Sink Finish', type: 'item' },
          { id: 'pot-filler', name: 'Pot Filler', type: 'item' },
          {
            id: 'pendant-light',
            name: 'Pendant Light',
            type: 'item',
            allowNone: true,
            options: [
              {
                id: 'pl-modern-globe',
                name: 'Modern Globe',
                thumbnail: T.a,
                selected: true,
              },
              { id: 'pl-industrial', name: 'Industrial', thumbnail: T.b },
              { id: 'pl-drum-shade', name: 'Drum Shade', thumbnail: T.c },
              { id: 'pl-mini-pendant', name: 'Mini Pendant', thumbnail: T.d },
              { id: 'pl-cluster', name: 'Cluster', thumbnail: T.e },
            ],
          },
          {
            id: 'under-cabinet-light',
            name: 'Under Cabinet Light',
            type: 'item',
            allowNone: true,
            options: [
              {
                id: 'ucl-led-strip',
                name: 'LED Strip',
                thumbnail: T.f,
                selected: true,
              },
              { id: 'ucl-puck', name: 'Puck Light', thumbnail: T.g },
              { id: 'ucl-linear-bar', name: 'Linear Bar', thumbnail: T.h },
            ],
          },
          {
            id: 'railing',
            name: 'Railing',
            type: 'item',
            options: [
              {
                id: 'rail-traditional',
                name: 'Traditional',
                thumbnail: T.a,
                selected: true,
              },
              { id: 'rail-modern', name: 'Modern', thumbnail: T.b },
              { id: 'rail-craftsman', name: 'Craftsman', thumbnail: T.c },
              { id: 'rail-glass-panel', name: 'Glass Panel', thumbnail: T.d },
              { id: 'rail-cable', name: 'Cable Rail', thumbnail: T.e },
            ],
            children: [
              {
                id: 'railing-post',
                name: 'Post',
                type: 'item',
                options: [
                  {
                    id: 'post-square',
                    name: 'Square Post',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'post-turned', name: 'Turned Post', thumbnail: T.b },
                  { id: 'post-craftsman', name: 'Craftsman', thumbnail: T.c },
                ],
              },
              {
                id: 'railing-cap',
                name: 'Post Cap',
                type: 'item',
                options: [
                  {
                    id: 'cap-flat',
                    name: 'Flat Cap',
                    thumbnail: T.d,
                    selected: true,
                  },
                  { id: 'cap-ball', name: 'Ball Cap', thumbnail: T.e },
                  { id: 'cap-acorn', name: 'Acorn Cap', thumbnail: T.f },
                ],
              },
              {
                id: 'railing-handrail',
                name: 'Handrail',
                type: 'item',
                options: [
                  {
                    id: 'hr-round',
                    name: 'Round Profile',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'hr-flat', name: 'Flat Profile', thumbnail: T.h },
                  { id: 'hr-craftsman', name: 'Craftsman', thumbnail: T.i },
                ],
              },
              {
                id: 'railing-baluster',
                name: 'Baluster',
                type: 'item',
                options: [
                  {
                    id: 'bal-plain',
                    name: 'Plain Square',
                    thumbnail: T.a,
                    selected: true,
                  },
                  { id: 'bal-twisted', name: 'Twisted Iron', thumbnail: T.b },
                  { id: 'bal-basket', name: 'Basket Weave', thumbnail: T.c },
                  { id: 'bal-wood', name: 'Wood Turned', thumbnail: T.d },
                ],
              },
              {
                id: 'railing-material',
                name: 'Material',
                type: 'item',
                options: [
                  {
                    id: 'rail-mat-oak',
                    name: 'White Oak',
                    thumbnail: T.g,
                    selected: true,
                  },
                  { id: 'rail-mat-maple', name: 'Maple', thumbnail: T.h },
                  { id: 'rail-mat-iron', name: 'Wrought Iron', thumbnail: T.i },
                  {
                    id: 'rail-mat-painted',
                    name: 'Painted White',
                    thumbnail: T.j,
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: 'bathroom',
        name: 'Bathroom',
        type: 'item',
      },
      {
        id: 'laundry',
        name: 'Laundry',
        type: 'item',
      },
      {
        id: 'refrigerators',
        name: 'Refrigerators',
        type: 'item',
        options: [
          {
            id: 'fridges-basic-28',
            name: 'Basic 28"',
            thumbnail: T.a,
            selected: true,
          },
          { id: 'fridges-standard-30', name: 'Standard 30"', thumbnail: T.b },
          { id: 'fridges-economy-24', name: 'Economy 24"', thumbnail: T.c },
          { id: 'fridges-stainless-30', name: 'Stainless 30"', thumbnail: T.d },
          { id: 'fridges-energy-star', name: 'Energy Star', thumbnail: T.e },
          { id: 'fridges-top-freezer', name: 'Top Freezer', thumbnail: T.f },
          {
            id: 'fridges-bottom-freezer',
            name: 'Bottom Freezer',
            thumbnail: T.a,
          },
          { id: 'fridges-compact-plus', name: 'Compact Plus', thumbnail: T.b },
          { id: 'fridges-french-33', name: 'French Door 33"', thumbnail: T.c },
          {
            id: 'fridges-side-by-side-36',
            name: 'Side-by-Side 36"',
            thumbnail: T.d,
          },
          {
            id: 'fridges-counter-33',
            name: 'Counter Depth 33"',
            thumbnail: T.e,
          },
          { id: 'fridges-glass-door', name: 'Glass Door', thumbnail: T.f },
          { id: 'fridges-column', name: 'Column Style', thumbnail: T.a },
          { id: 'fridges-smart-pro', name: 'Smart Hub Pro', thumbnail: T.e },
          { id: 'fridges-panel-36', name: 'Panel Ready 36"', thumbnail: T.f },
          { id: 'fridges-builtin-42', name: 'Built-In 42"', thumbnail: T.b },
          { id: 'fridges-pro-48', name: 'Professional 48"', thumbnail: T.c },
          { id: 'fridges-chef', name: 'Chef Series', thumbnail: T.d },
          { id: 'fridges-subzero-48', name: 'Sub-Zero 48"', thumbnail: T.f },
          {
            id: 'fridges-thermador',
            name: 'Thermador Freedom',
            thumbnail: T.a,
          },
          { id: 'fridges-viking', name: 'Viking Professional', thumbnail: T.b },
          { id: 'fridges-lacornue', name: 'La Cornue', thumbnail: T.c },
        ],
      },
      {
        id: 'refrigerators-lots',
        name: 'Refrigerators Lots',
        type: 'item',
        options: (() => {
          const baseOptions = [
            {
              id: 'fridges-basic-28',
              name: 'Basic 28"',
              thumbnail: T.a,
              selected: true,
            },
            { id: 'fridges-standard-30', name: 'Standard 30"', thumbnail: T.b },
            { id: 'fridges-economy-24', name: 'Economy 24"', thumbnail: T.c },
            {
              id: 'fridges-stainless-30',
              name: 'Stainless 30"',
              thumbnail: T.d,
            },
            { id: 'fridges-energy-star', name: 'Energy Star', thumbnail: T.e },
            { id: 'fridges-top-freezer', name: 'Top Freezer', thumbnail: T.f },
            {
              id: 'fridges-bottom-freezer',
              name: 'Bottom Freezer',
              thumbnail: T.a,
            },
            {
              id: 'fridges-compact-plus',
              name: 'Compact Plus',
              thumbnail: T.b,
            },
            {
              id: 'fridges-french-33',
              name: 'French Door 33"',
              thumbnail: T.c,
            },
            {
              id: 'fridges-side-by-side-36',
              name: 'Side-by-Side 36"',
              thumbnail: T.d,
            },
            {
              id: 'fridges-counter-33',
              name: 'Counter Depth 33"',
              thumbnail: T.e,
            },
            { id: 'fridges-glass-door', name: 'Glass Door', thumbnail: T.f },
            { id: 'fridges-column', name: 'Column Style', thumbnail: T.a },
            { id: 'fridges-smart-pro', name: 'Smart Hub Pro', thumbnail: T.e },
            { id: 'fridges-panel-36', name: 'Panel Ready 36"', thumbnail: T.f },
            { id: 'fridges-builtin-42', name: 'Built-In 42"', thumbnail: T.b },
            { id: 'fridges-pro-48', name: 'Professional 48"', thumbnail: T.c },
            { id: 'fridges-chef', name: 'Chef Series', thumbnail: T.d },
            { id: 'fridges-subzero-48', name: 'Sub-Zero 48"', thumbnail: T.f },
            {
              id: 'fridges-thermador',
              name: 'Thermador Freedom',
              thumbnail: T.a,
            },
            {
              id: 'fridges-viking',
              name: 'Viking Professional',
              thumbnail: T.b,
            },
            { id: 'fridges-lacornue', name: 'La Cornue', thumbnail: T.c },
          ]
          const result: typeof baseOptions = []
          for (let copy = 0; copy < 5; copy++) {
            for (const opt of baseOptions) {
              if (result.length >= 100) break
              result.push({
                ...opt,
                id:
                  copy === 0 ? `lots-${opt.id}` : `lots-${opt.id}-${copy + 1}`,
                selected: copy === 0 && opt.selected ? true : undefined,
              })
            }
            if (result.length >= 100) break
          }
          return result
        })(),
      },
    ],
  },
  {
    id: 'basement-options',
    name: 'Basement Options',
    type: 'category',
    children: [
      { id: 'basement-general', name: 'General', type: 'item' },
      { id: 'basement-kitchen', name: 'Kitchen', type: 'item' },
      { id: 'basement-bathroom', name: 'Bathroom', type: 'item' },
      { id: 'basement-laundry', name: 'Laundry', type: 'item' },
    ],
  },
]

export const exteriorMenuData: InteriorMenuItem[] = [
  {
    id: 'ext-main-options',
    name: 'Main Options',
    type: 'category',
    children: [
      {
        id: 'elevations',
        name: 'Elevations',
        type: 'item',
        children: [
          {
            id: 'ext-options',
            name: 'Options',
            type: 'item',
          },
        ],
      },
      {
        id: 'inspirations',
        name: 'Inspirations',
        type: 'item',
        children: [
          {
            id: 'ext-palettes',
            name: 'Palettes',
            type: 'item',
          },
        ],
      },
    ],
  },
]
